"use client";

import type {
  DialogAnimation,
  DialogCloseButtonVariant,
  DialogHeight,
  DialogResponsive,
  DialogWidth,
} from "../../types/dialog";

import Close from "@homeinstead/icons/Close";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { cn } from "../../utils/classnames";
import {
  DIALOG_ANIMATION_CLASSES,
  DIALOG_CONTENT_FULLSCREEN_CLASSES,
  DIALOG_HEIGHT_CLASSES,
  DIALOG_OVERLAY_FULLSCREEN_CLASSES,
  DIALOG_VIEWPORT_FULLSCREEN_CLASSES,
  DIALOG_WIDTH_CLASSES,
  dialogCloseButtonVariants,
  resolveResponsiveClasses,
} from "./dialog-variants";

const Dialog = DialogPrimitive.Root;

const DialogBody = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    className={cn("min-h-0 flex-1 overflow-y-auto px-6 pb-6", className)}
    {...props}
  />
);
DialogBody.displayName = "DialogBody";

const DialogClose = DialogPrimitive.Close;

type DialogCloseButtonProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Close
> & {
  iconClassName?: string;
  variant?: DialogCloseButtonVariant;
};

const DialogCloseButton = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Close>,
  DialogCloseButtonProps
>(({ className, iconClassName, variant, ...props }, ref) => (
  <DialogPrimitive.Close
    aria-label="Close dialog"
    className={cn(dialogCloseButtonVariants({ variant }), className)}
    ref={ref}
    {...props}
  >
    {/*
     * The Close glyph hardcodes `fill="#2C4F26"`, which disappears against the
     * `solid` variant. A CSS rule outranks the presentation attribute, so this
     * hands tint control back to the button's text color.
     */}
    <Close
      className={cn("size-4 shrink-0 [&_path]:fill-current", iconClassName)}
    />
  </DialogPrimitive.Close>
));
DialogCloseButton.displayName = "DialogCloseButton";

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  /**
   * Opt into an enter/exit animation. Defaults to `"none"` — the dialog
   * appears instantly. Backed by `DIALOG_ANIMATION_CLASSES` in
   * `dialog-variants.ts`; the overlay gets the `fade` pair whenever this
   * isn't `"none"`, so a visible (non-fullscreen) backdrop doesn't pop in
   * against an animating panel.
   */
  animation?: DialogAnimation;
  /**
   * Overrides tailwindcss-animate's default animation duration (150ms).
   * Applied as an inline style, not a `duration-[...]` class — Tailwind
   * can't generate arbitrary-value classes from a runtime number, the same
   * constraint `LeadFormStepWrapper` documents for its CMS-driven step
   * transition duration. Ignored when `animation` is `"none"`.
   */
  animationDurationMs?: number;
  container?: React.ComponentProps<typeof DialogPrimitive.Portal>["container"];
  fullscreen?: DialogResponsive<boolean>;
  height?: DialogResponsive<DialogHeight>;
  overlayClassName?: string;
  width?: DialogResponsive<DialogWidth>;
};

/**
 * Renders its own portal, backdrop, and centering viewport. The panel is nested
 * inside the backdrop so Radix's dismissable layer counts backdrop pointerdowns
 * as outside interactions, and so fullscreen only has to drop the backdrop's
 * paint and padding.
 */
const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      animation = "none",
      animationDurationMs,
      children,
      className,
      container,
      fullscreen = false,
      height = "auto",
      overlayClassName,
      style,
      width = "md",
      ...props
    },
    ref,
  ) => {
    // Duration-only for the overlay — it never takes a caller `style`, so
    // there is nothing to merge. The panel gets the same duration plus
    // whatever `style` the caller passed for it, caller values winning on
    // conflict.
    const durationStyle =
      animation !== "none" && animationDurationMs !== undefined
        ? { animationDuration: `${animationDurationMs}ms` }
        : undefined;
    const contentStyle = durationStyle ? { ...durationStyle, ...style } : style;

    return (
      <DialogPortal container={container}>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[999] overflow-y-auto overscroll-contain bg-black/40",
            animation !== "none" && DIALOG_ANIMATION_CLASSES.fade,
            resolveResponsiveClasses(
              fullscreen,
              DIALOG_OVERLAY_FULLSCREEN_CLASSES,
            ),
            overlayClassName,
          )}
          style={durationStyle}
        >
          <div
            className={cn(
              "flex min-h-full items-center justify-center p-4",
              resolveResponsiveClasses(
                fullscreen,
                DIALOG_VIEWPORT_FULLSCREEN_CLASSES,
              ),
            )}
          >
            <DialogPrimitive.Content
              // Radix hides outside content with `aria-hidden` instead of
              // annotating the panel; state it explicitly as well so audits and
              // older screen readers both see modal semantics.
              aria-modal="true"
              className={cn(
                "relative flex max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl bg-white shadow-xl outline-none",
                DIALOG_ANIMATION_CLASSES[animation],
                resolveResponsiveClasses(width, DIALOG_WIDTH_CLASSES),
                resolveResponsiveClasses(height, DIALOG_HEIGHT_CLASSES),
                resolveResponsiveClasses(
                  fullscreen,
                  DIALOG_CONTENT_FULLSCREEN_CLASSES,
                ),
                className,
              )}
              ref={ref}
              style={contentStyle}
              {...props}
            >
              {children}
            </DialogPrimitive.Content>
          </div>
        </DialogPrimitive.Overlay>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = "DialogContent";

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    className={cn("text-base text-new-beginnings-base", className)}
    ref={ref}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex shrink-0 flex-col gap-2 p-6 pt-0 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex shrink-0 flex-col gap-2 p-6 pr-16", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogPortal = DialogPrimitive.Portal;

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    className={cn(
      "font-jakarta font-semibold text-new-beginnings-base text-xl leading-tight",
      className,
    )}
    ref={ref}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogTrigger = DialogPrimitive.Trigger;

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
